import { Dispatch, SetStateAction, useState } from 'react';

import { FULL_PERCENT, PROGRESS_INTERVAL } from '@hooks/constants';
import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setChartsReceivedAt } from '@slices/kpi';

import { getKpiCharts, getProgress, runProcessKpi, startProgress } from '@api/kpi';

import { handleErrorFromMessage } from '@utils/handleRequestError';
import { getDateWithoutOffset } from '@utils/kpi-utils/date';

import { IResponseError } from '@interfaces/errors/apiErrors';
import { IKpiChartsDTO, IProgressKpiDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IChart } from '@interfaces/kpi/kpiCommon';

import { EErrorCodes } from '@enums/errorCodes';

import {
	DRILL_DOWN_COMPARE_CHARTS,
	KPI_CHARTS,
	KPI_PROGRESS,
	KPI_START_PROGRESS,
} from '@constants/queryKeys';

import { useMutateKpiChartsDrillDown } from './useMutateKpiChartsDrillDown';

export const useMutateKpiCharts = (setErrorText: Dispatch<SetStateAction<string | undefined>>) => {
	const dispatch = useAppDispatch();

	const [refetchIntervalProgress, setRefetchIntervalProgress]
		= useState<number | false>(PROGRESS_INTERVAL);
	const [chartData, setChartData] = useState<IKpiChartsDTO | undefined>();

	const resetIntervals = () => setRefetchIntervalProgress(PROGRESS_INTERVAL);

	const {
		runDrillDownProgress,
		drillDownDataCharts,
		isLoadingDrillDownCharts,
	} = useMutateKpiChartsDrillDown(setErrorText);

	const {
		data: progressId,
		mutate: runProgress,
		isLoading: isLoadingProgress,
	} = useMutation<IKpiChartsDTO>({
		mutationKey: [KPI_START_PROGRESS],
		mutationFn: startProgress,
		onSuccess: async (progressId: string, variables) => {
			await setChartData(variables);
			await runProcessKpi({ ...variables, progressId });
			await resetIntervals();
		},
	});

	const { data: progress } = useQuery<IProgressKpiDTO>({
		queryKey: [KPI_PROGRESS, progressId],
		queryFn: () => getProgress(progressId!),
		refetchInterval: refetchIntervalProgress,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		onSuccess: (data: IProgressKpiDTO) => {
			if (data.percentsProgress !== null
				&& Math.floor(data.percentsProgress) === FULL_PERCENT
				&& !data.isDataLoadingInProgress) {
				const isFullProgress = progressId === data?.id && data?.percentsProgress === FULL_PERCENT;
				if (isFullProgress && chartData) {
					getCharts({ ...chartData, progressId });
					setRefetchIntervalProgress(false);

				}

			}
		},
		enabled: [!!progressId],
		onError: (err: IResponseError) => {
			setRefetchIntervalProgress(false);
			handleErrorFromMessage(err);
		},
	});

	const {
		data: dataCharts,
		mutate: getCharts,
		isLoading: isLoadingCharts,
	} = useMutation<IKpiChartsDTO>({
		mutationKey: [KPI_CHARTS],
		mutationFn: (data: IKpiChartsDTO) => {
			setErrorText(undefined);

			return getKpiCharts({ ...data });
		},
		select: (charts: Array<IChart>) => {
			return charts.map(chat => ({ ...chat, isOpen: true, isThreshold: false }));
		},
		refetchOnWindowFocus: false,
		onSuccess: () => {
			dispatch(setChartsReceivedAt(getDateWithoutOffset(new Date().getTime()).getTime()));
		},
		onError: (err: IResponseError) => {
			try {
				if (err.errors[0].code === EErrorCodes.couldNotApplyKpiToSites) {
					setErrorText(err.errors[0].message);
				} else if (err.errors[0].code === EErrorCodes.notFoundAggregationType) {
					setErrorText('No KPI charts available');
				} else {
					setErrorText(undefined);

					handleErrorFromMessage(err);
				}
			} catch {
				handleErrorFromMessage(err);
			}
		},
	});

	const {
		data: compareDrillDownCharts,
		mutate: getDrillDownCompareCharts,
		isLoading: isLoadingDrillDownCompareCharts,
	} = useMutation<Array<IKpiChartsDTO>>({
		mutationKey: [DRILL_DOWN_COMPARE_CHARTS],
		mutationFn: (chartsData: Array<IKpiChartsDTO>) => Promise.all(chartsData.map(data => getKpiCharts(data))),
	});

	return {
		compareDrillDownCharts,
		dataCharts,
		dataDrillDownCharts: drillDownDataCharts,
		getCharts,
		getDrillDownCharts: runDrillDownProgress,
		getDrillDownCompareCharts,
		isLoadingCharts: isLoadingCharts || isLoadingProgress,
		isLoadingDrillDownCharts: isLoadingDrillDownCharts,
		isLoadingDrillDownCompareCharts,
		runProgress,
		progress,
	};
};
